/* eslint-disable complexity */
import { pxToEm } from 'util/pxToEm';
import { useTheme, Box, Typography, useMediaQuery } from '@mui/material';
import {
  ArrowBackIosRounded,
  ArrowForwardIosRounded,
} from '@mui/icons-material';
import { Image, Link, TruncateText } from 'components/lib';
import React, { ReactElement, useEffect, useState } from 'react';
import { mapValues } from 'lodash';
import { useIsLoggedIn } from 'hooks';

export interface SchoolReelProps {
  schools: {
    id: string;
    iconUrl: string;
    logoUrl: string;
    name: string;
    link: string;
  }[];
  onSchoolClick?(schoolId: string): void;
  additionalWidth?: number;
}

const REEL_ELEMENT_WIDTH = 208;
const REEL_ELEMENT_HEIGHT = 64;
const REEL_GAP = 32;

export const SchoolReel = ({
  schools,
  onSchoolClick,
  additionalWidth = 0,
}: SchoolReelProps): ReactElement => {
  const theme = useTheme();
  const isLoggedIn = useIsLoggedIn();

  const [offset, setOffset] = useState<number | null>(null);
  const [isAnimating, setAnimating] = useState(false);

  const getMediaQueryForNumColumns = (numColumns: number) => {
    const padding = 120;
    const width =
      numColumns * (REEL_ELEMENT_WIDTH + REEL_GAP) + padding + additionalWidth;
    return theme.breakpoints.up(width);
  };

  const minColumnsForWideDisplay = 3;
  const numColumnsForScreenWidth: Record<string, number> = {};
  for (let i = minColumnsForWideDisplay; i <= 20; i++)
    numColumnsForScreenWidth[getMediaQueryForNumColumns(i)] = i;

  let numColumns = minColumnsForWideDisplay;
  for (const mediaQuery in numColumnsForScreenWidth) {
    if (useMediaQuery(mediaQuery))
      numColumns = numColumnsForScreenWidth[mediaQuery];
  }

  const breakpointMediaQuery = getMediaQueryForNumColumns(
    minColumnsForWideDisplay,
  );

  useEffect(() => {
    if (offset !== null) setAnimating(true);
  }, [offset]);

  const schoolLogo = (logoUrl: string, schoolName: string) =>
    logoUrl ? (
      <Image
        alt={schoolName}
        src={logoUrl}
        style={{
          maxWidth: 176,
          maxHeight: 48,
        }}
      />
    ) : (
      <Image
        src="/img/schoolLogoPlaceholderSmall.svg"
        alt="School Logo Placeholder"
        style={{
          maxWidth: 60,
          maxHeight: 60,
        }}
      />
    );

  const showSchools = () => {
    return schools.map((school, i) => (
      <React.Fragment key={school.id}>
        <Link
          href={isLoggedIn ? school.link : ''}
          onClick={() => {
            onSchoolClick && onSchoolClick(school.id);
          }}
          tabIndex={
            i >= (offset ?? 0) && i < (offset ?? 0) + numColumns ? 0 : -1
          }
        >
          <Box
            sx={{
              width: REEL_ELEMENT_WIDTH,
              height: REEL_ELEMENT_HEIGHT,
              py: 16,
              px: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #ddd',
              borderRadius: 6,
              background: '#fff',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              },
            }}
          >
            <Box maxWidth="100%" display="flex" alignItems="center">
              {schoolLogo(school.logoUrl, school.name)}
              {!school.logoUrl && (
                <Box ml={10} minWidth={0} fontSize="0.875em">
                  <TruncateText lines={2}>
                    <Typography
                      color="primary"
                      style={{
                        lineHeight: '1.25em',
                        fontWeight: 500,
                      }}
                    >
                      {school.name}
                    </Typography>
                  </TruncateText>
                </Box>
              )}
            </Box>
          </Box>
        </Link>
      </React.Fragment>
    ));
  };

  return (
    <Box
      fontSize="clamp(14px, 1rem, 24px)"
      display="flex"
      alignItems="center"
      sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
    >
      <Box
        sx={{
          gap: `${REEL_GAP}px`,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: 20,
          [breakpointMediaQuery]: { display: 'none' },
          scrollbarWidth: 'none', // For Firefox
          '&::-webkit-scrollbar': {
            display: 'none', // For Chrome, Safari, and Edge
          },
          '& > *': {
            flexShrink: 0,
          },
        }}
      >
        {showSchools()}
      </Box>
      <Box
        sx={{
          display: 'none',
          [breakpointMediaQuery]: { display: 'flex' },
        }}
        mx="auto"
        alignItems="center"
      >
        <Box
          width={pxToEm(25)}
          height={pxToEm(25)}
          display="flex"
          justifyContent="center"
          alignItems="center"
          tabIndex={0}
          className="keyboardFocus"
          style={{
            zIndex: 3,
            cursor: (offset ?? 0) === 0 ? 'default' : 'pointer',
            borderRadius: pxToEm(25 / 2),
            background:
              (offset ?? 0) === 0
                ? theme.palette.neutral.darker
                : theme.palette.primary.main,
          }}
          onClick={() => setOffset(Math.max(0, (offset ?? 0) - numColumns))}
          onKeyPress={(e: React.KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setOffset(Math.max(0, (offset ?? 0) - numColumns));
            }
          }}
        >
          <ArrowBackIosRounded style={{ fontSize: '1.2em', color: '#fff' }} />
        </Box>
        <Box
          mr={isAnimating ? -pxToEm(25) : 0}
          width={isAnimating ? `calc(${REEL_GAP}px + ${pxToEm(25)})` : REEL_GAP}
          style={{
            zIndex: 2,
            alignSelf: 'stretch',
            background:
              'linear-gradient(90deg, #fcfcffff 0%, #fcfcff33 50%, #fcfcff00 100%)',
          }}
        >
          &nbsp;
        </Box>
        <Box
          mx={isAnimating ? `calc(-${REEL_GAP}px - ${pxToEm(25)})` : -REEL_GAP}
          sx={{
            ...mapValues(numColumnsForScreenWidth, columns => ({
              width: `calc(${columns} * ${
                REEL_ELEMENT_WIDTH + REEL_GAP
              }px + 20px + ${isAnimating ? pxToEm(25) : '0px'} - 5px)`,
            })),
          }}
          my={-10}
          style={{
            overflowX: 'hidden',
            overflowY: 'visible',
          }}
        >
          <Box
            pl={0}
            ml={(offset ?? 0) * -(REEL_ELEMENT_WIDTH + REEL_GAP) + REEL_GAP}
            onTransitionEnd={() => setAnimating(false)}
            display="flex"
            flexWrap="nowrap"
            style={{
              gap: REEL_GAP,
              transition: `margin-left ${
                0.625 * (Math.max(2, Math.min(numColumns, 8)) / 5)
              }s ease-in-out`,
              willChange: 'margin-left',
            }}
          >
            {showSchools()}
          </Box>
        </Box>
        <Box
          ml={isAnimating ? pxToEm(-25) : 0}
          width={isAnimating ? `calc(${REEL_GAP}px + ${pxToEm(25)})` : REEL_GAP}
          style={{
            zIndex: 2,
            alignSelf: 'stretch',
            background:
              'linear-gradient(90deg, #fcfcff00 0%, #fcfcff11 50%, #fcfcffff 100%)',
          }}
        >
          &nbsp;
        </Box>
        <Box
          width={pxToEm(25)}
          height={pxToEm(25)}
          display="flex"
          justifyContent="center"
          alignItems="center"
          tabIndex={0}
          className="keyboardFocus"
          style={{
            zIndex: 3,
            cursor:
              (offset ?? 0) >= schools.length - numColumns
                ? 'default'
                : 'pointer',
            borderRadius: pxToEm(25 / 2),
            background:
              (offset ?? 0) >= schools.length - numColumns
                ? theme.palette.neutral.darker
                : theme.palette.primary.main,
          }}
          onClick={() =>
            setOffset(
              Math.min(schools.length - numColumns, (offset ?? 0) + numColumns),
            )
          }
          onKeyPress={(e: React.KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setOffset(
                Math.min(
                  schools.length - numColumns,
                  (offset ?? 0) + numColumns,
                ),
              );
            }
          }}
        >
          <ArrowForwardIosRounded
            style={{ fontSize: '1.2em', color: '#fff' }}
          />
        </Box>
      </Box>
    </Box>
  );
};
